import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { userService } from '@/services/userService';
import { useToast } from '@/context/ToastContext';
import { useAuth } from '@/hooks/useAuth';
import { getErrorMessage } from '@/api/api';
import { useUiStore } from '@/store/uiStore';

interface ApiErrorWithStatus {
  status?: number;
  detail?: string;
}

export const ComingSoonModal: React.FC = () => {
  const isOpen = useUiStore((state) => state.modals.comingSoon);
  const closeModal = useUiStore((state) => state.closeModal);
  const { addToast } = useToast();
  const { user, updateUser } = useAuth();
  const [isLoading, setIsLoading] = React.useState(false);

  const handleVipJoin = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const response = await userService.joinVipWishlist();

      // Update local user state with the new timestamp
      updateUser({ ...user, vip_wishlist_at: response.vip_wishlist_at });

      addToast({
        title: 'VIP List Joined!',
        subtitle: response.message,
        type: 'success',
      });

      closeModal('comingSoon');
    } catch (error) {
      const err = error as ApiErrorWithStatus;

      // Handle "Already on wishlist" (400) specifically
      if (err.status === 400) {
        addToast({
          title: 'Already Joined',
          subtitle: err.detail || 'You are already on the VIP wishlist.',
          type: 'info',
        });
        closeModal('comingSoon');
      } else {
        addToast({
          title: 'Error',
          subtitle: getErrorMessage(error),
          type: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // If user is already on the wishlist, we can change the button text or state
  const isOnWishlist = !!user?.vip_wishlist_at;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className='fixed inset-0 z-modal flex items-center justify-center p-4'>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className='absolute inset-0 bg-black/40 backdrop-blur-sm'
            onClick={() => closeModal('comingSoon')}
          />

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className={clsx(
              'relative flex w-full max-w-[388px] flex-col overflow-hidden',
              'max-h-[90vh]',
              'rounded-20',
              'shadow-2xl'
            )}
            style={{
              background: `linear-gradient(180deg, rgba(255, 255, 255, 0) -1.92%, #A28DF6 140.84%), #FFFFFF`,
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => closeModal('comingSoon')}
              className='absolute right-4 top-4 z-10 rounded-full bg-red-50 p-2 text-red-400 transition-colors hover:bg-red-100'
              aria-label='Close'
            >
              <XMarkIcon className='size-5' />
            </button>

            <div className='flex flex-col items-center overflow-y-auto px-8 pb-[38px] pt-12 text-center'>
              <div className='flex flex-col gap-2'>
                <h2 className='font-proxima text-24 font-bold leading-27 text-brand-800'>
                  Create your Echo
                  <br />
                  Coming Soon
                </h2>
                <p className='mb-[34px] mt-[17px] font-sans text-16 font-normal leading-21 text-gray-600'>
                  We’re putting on the final touches.
                </p>
              </div>

              <Button
                onClick={handleVipJoin}
                disabled={isLoading || isOnWishlist}
                isLoading={isLoading}
                variant='settings-modify'
                className='h-[44px] w-full max-w-[202px] text-16 font-bold uppercase tracking-wide text-brand-800'
              >
                {isOnWishlist ? 'ALREADY ON LIST' : 'JOIN the VIP list'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
